/* eslint-disable max-lines */
import {
  request_add_organization_member,
  request_bulk_add_organization_members,
  request_invite_organization_member,
} from "./organization_members_api";
import {
  EMAIL_PATTERN,
  PHONE_PATTERN,
  build_bulk_member_payload,
  collect_validation_errors,
  create_empty_bulk_member,
  format_bulk_member_error,
  submit_bulk_members,
  validate_bulk_member_entries,
} from "./organization_members_helpers";

import {
  get_active_organization_id,
} from "@/utils/organization_profile";

function normalize_invite_method(invite_method) {
  switch (invite_method) {
    case "link":
    case "qr_code":
      return invite_method;
    default:
      return "email";
  }
}

function get_pending_invite_label(invite_method) {
  return invite_method === "email" ? "Sending..." : "Generating...";
}

function get_idle_invite_label(invite_method) {
  switch (invite_method) {
    case "link":
      return "Generate Link";
    case "qr_code":
      return "Generate QR Code";
    default:
      return "Send Email Invite";
  }
}

function get_invite_method_label(invite_method, is_pending) {
  const safe_invite_method = normalize_invite_method(invite_method);
  return is_pending
    ? get_pending_invite_label(safe_invite_method)
    : get_idle_invite_label(safe_invite_method);
}

function first_present(...values) {
  return values.find((value) => value !== undefined && value !== null && String(value).trim() !== "") || "";
}

const INVITATION_SHARE_URL_TEMPLATES = new Map([
  ["facebook", "https://www.facebook.com/sharer/sharer.php?u={url}"],
  ["linkedin", "https://www.linkedin.com/sharing/share-offsite/?url={url}"],
  ["whatsapp", "https://api.whatsapp.com/send?text={text}"],
]);

export default {
  collect_validation_errors(rules) {
    return collect_validation_errors(rules);
  },
  reset_bulk_member_form() {
    this.bulk_members = [create_empty_bulk_member()];
    this.bulk_member_errors = {
      form: "",
      members: [],
    };
  },
  add_bulk_member_row() {
    this.bulk_members.push(create_empty_bulk_member());
    this.bulk_member_errors.members.push({});
  },
  remove_bulk_member_row(index) {
    if (this.bulk_members.length === 1) {
      this.bulk_members = [create_empty_bulk_member()];
      this.bulk_member_errors.members = [{}];
      return;
    }
    this.bulk_members.splice(index, 1);
    this.bulk_member_errors.members.splice(index, 1);
  },
  validate_bulk_members() {
    const validation = validate_bulk_member_entries(this.bulk_members, get_active_organization_id());
    this.bulk_member_errors = validation.errors;
    return validation.valid;
  },
  validate_new_user() {
    const email = String(this.new_user.email || "").trim();
    const phone_number = String(this.new_user.phone_number || "").trim();
    const organization_id = get_active_organization_id();
    const rules = [
      {
        field: "first_name",
        is_invalid: () => !this.new_user.first_name.trim(),
        message: "First name is required.",
      },
      {
        field: "last_name",
        is_invalid: () => !this.new_user.last_name.trim(),
        message: "Last name is required.",
      },
      {
        field: "email",
        is_invalid: () => !email,
        message: "Email is required.",
      },
      {
        field: "email",
        is_invalid: () => !!email && !EMAIL_PATTERN.test(email),
        message: "Please enter a valid email address.",
      },
      {
        field: "phone_number",
        is_invalid: () => !!phone_number && !PHONE_PATTERN.test(phone_number),
        message: "Please enter a valid phone number.",
      },
      {
        field: "role",
        is_invalid: () => !this.new_user.role,
        message: "User role is required.",
      },
      {
        field: "form",
        is_invalid: () => !organization_id,
        message: "Organisation id is missing. Please refresh and try again.",
      },
    ];
    const errors = this.collect_validation_errors(rules);

    this.new_user_errors = errors;
    return Object.keys(errors).length === 0;
  },
  reset_new_user_form() {
    this.new_user = {
      first_name: "",
      last_name: "",
      email: "",
      phone_number: "",
      role: "user",
    };
    this.new_user_errors = {};
  },
  validate_invite_form() {
    const email = String(this.invite_user.email || "").trim();
    const organization_id = get_active_organization_id();
    const rules = [
      {
        field: "email",
        is_invalid: () => this.invite_method_is("email") && !email,
        message: "Email is required.",
      },
      {
        field: "email",
        is_invalid: () => this.invite_method_is("email") && !!email && !EMAIL_PATTERN.test(email),
        message: "Please enter a valid email address.",
      },
      {
        field: "form",
        is_invalid: () => !organization_id,
        message: "Organisation id is missing. Please refresh and try again.",
      },
    ];

    this.invite_errors = this.collect_validation_errors(rules);
    return Object.keys(this.invite_errors).length === 0;
  },
  reset_invite_form() {
    this.invite_user = {
      email: "",
      role: "user",
      invite_method: "email",
    };
    this.invite_errors = {};
    this.latest_invitation = null;
  },
  open_invite_member_modal() {
    this.reset_invite_form();
    $("#add_organization_member_modal").modal("hide");
    setTimeout(() => $("#invite_organization_member_modal").modal("show"), 150);
  },
  open_add_member_modal() {
    this.reset_bulk_member_form();
    $("#invite_organization_member_modal").modal("hide");
    setTimeout(() => $("#add_organization_member_modal").modal("show"), 150);
  },
  set_invite_method(invite_method) {
    this.invite_user.invite_method = invite_method;
    this.latest_invitation = null;
  },
  invite_method_is(invite_method) {
    return this.invite_user.invite_method === invite_method;
  },
  get_invite_submit_label() {
    return get_invite_method_label(
      this.invite_user.invite_method,
      this.is_inviting_member
    );
  },
  invite_organization_member() {
    if (!this.validate_invite_form()) return Promise.resolve();

    this.is_inviting_member = true;
    return request_invite_organization_member(this.get_invite_member_payload())
      .then((response) => this.handle_invite_member_response(response))
      .catch((xhr) => this.set_invite_error(xhr?.responseJSON?.message || "Could not send invite."))
      .finally(() => {
        this.is_inviting_member = false;
      });
  },
  get_invite_member_payload() {
    const invite_method = this.invite_user.invite_method || "email";
    return {
      email: this.get_invite_member_email(invite_method),
      role: this.invite_user.role || null,
      invite_method,
      organization_id: get_active_organization_id(),
    };
  },
  get_invite_member_email(invite_method) {
    if (invite_method !== "email") return "";
    return String(this.invite_user.email || "").trim();
  },
  handle_invite_member_response(response) {
    return this.is_success_response(response)
      ? this.complete_invite_member_flow(response)
      : this.set_invite_error(response?.message || "Could not send invite.");
  },
  is_success_response(response) {
    return response?.status === "success";
  },
  set_latest_invitation(invite_method, response) {
    this.latest_invitation = invite_method === "email"
      ? null
      : this.build_invitation_share_data(response);
  },
  close_email_invite_modal(invite_method) {
    if (invite_method === "email") {
      $("#invite_organization_member_modal").modal("hide");
    }
  },
  complete_invite_member_flow(response) {
    const invite_method = normalize_invite_method(this.invite_user.invite_method);
    this.set_latest_invitation(invite_method, response);
    this.invite_user.email = "";
    this.invite_errors = {};
    this.show_success(response?.message || "Invite created");
    this.close_email_invite_modal(invite_method);
    this.fetch_organization_members();
  },
  build_invitation_share_data(response = {}) {
    const member = response.member || {};
    return {
      email: member.email || "",
      link: first_present(response.invitation_link, member.invitation_link),
      qr_code: first_present(response.invitation_qr_code, member.invitation_qr_code),
    };
  },
  invitation_qr_src(invitation = this.latest_invitation) {
    const qr_code = invitation?.qr_code || "";
    if (!qr_code) return "";
    return qr_code.startsWith("data:image") ? qr_code : `data:image/png;base64,${qr_code}`;
  },
  async copy_invitation_link() {
    const link = this.latest_invitation?.link;
    if (!link) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(link);
      } else {
        this.copy_text_with_fallback(link);
      }
      this.show_success("Invite link copied");
    } catch {
      this.set_invite_error("Could not copy invite link.");
    }
  },
  get_invitation_share_text() {
    return `Join ${this.organization?.company_name || "our organisation"} on ${this.$clientName || "the platform"}`;
  },
  get_invitation_share_url(platform) {
    const link = this.latest_invitation?.link || "#";
    const encoded_link = encodeURIComponent(link);
    const encoded_text = encodeURIComponent(`${this.get_invitation_share_text()} - ${link}`);
    const template = INVITATION_SHARE_URL_TEMPLATES.get(String(platform || "")) || "{url}";

    return template
      .replace("{url}", encoded_link)
      .replace("{text}", encoded_text);
  },
  copy_text_with_fallback(text) {
    const input = document.createElement("textarea");
    input.value = text;
    input.setAttribute("readonly", "");
    input.style.position = "fixed";
    input.style.opacity = "0";
    document.body.appendChild(input);
    input.select();
    document.execCommand("copy");
    document.body.removeChild(input);
  },
  download_invitation_qr() {
    const qr_src = this.invitation_qr_src();
    if (!qr_src) return;
    const link = document.createElement("a");
    link.href = qr_src;
    link.download = "organization-invite-qr.png";
    link.click();
  },
  get_new_member_payload() {
    return ({
      first_name: String(this.new_user.first_name || "").trim(),
      last_name: String(this.new_user.last_name || "").trim(),
      email: String(this.new_user.email || "").trim(),
      organization_id: get_active_organization_id(),
    });
  },
  complete_add_member_flow() {
    this.reset_new_user_form();
    this.show_success("Member added");
    $("#add_organization_member_modal").modal("hide");
    this.fetch_organization_members();
  },
  get_bulk_member_payload() {
    return build_bulk_member_payload(get_active_organization_id(), this.bulk_members);
  },
  complete_bulk_add_member_flow(member_count) {
    this.reset_bulk_member_form();
    this.show_success(member_count > 1 ? `${member_count} members added` : "Member added");
    $("#add_organization_member_modal").modal("hide");
    this.fetch_organization_members();
  },
  bulk_add_organization_members() {
    return this.validate_bulk_members() ? this.submit_valid_bulk_members() : Promise.resolve();
  },
  submit_valid_bulk_members() {
    this.is_bulk_adding_members = true;
    const payload = this.get_bulk_member_payload();
    return submit_bulk_members(
      payload,
      request_bulk_add_organization_members,
      request_add_organization_member
    )
      .then((member_count) => this.complete_bulk_add_member_flow(member_count))
      .catch((xhr) => {
        this.bulk_member_errors.form = xhr?.message || format_bulk_member_error(xhr?.responseJSON || {});
      })
      .finally(() => {
        this.is_bulk_adding_members = false;
      });
  },
  create_user_and_add_member() {
    if (!this.validate_new_user()) return Promise.resolve();

    this.is_creating_user = true;
    return request_add_organization_member(this.get_new_member_payload())
      .then((response) => this.handle_add_member_response(response))
      .catch((xhr) => this.set_new_user_error(xhr?.responseJSON?.message || "Could not add organisation member."))
      .finally(() => {
        this.is_creating_user = false;
      });
  },
  handle_add_member_response(response) {
    return this.is_success_response(response)
      ? this.complete_add_member_flow()
      : this.set_new_user_error(response?.message || "Could not add organisation member.");
  },
};
